import {
  Component,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DEPLOY_ENV } from '../../environments/deploy-env';
import { CountryToken, EnvToken, LanguageToken } from '@app/core/env.tokens';
import { Meta, Title } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';

import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';

import { HeaderComponent } from '@components/header/header.component';
import { IncidentNoticeComponent } from '@components/shared/incident-notice/incident-notice.component';
import { Subject } from 'rxjs';

import { CookieService } from '@services/logic/cookie.service';
import { EventService } from '@services/logic/event.service';
import { PosthogService } from '@services/logic/posthog.service';
import { StorageService } from '@services/logic/storage.service';

@Component({
  selector: 'app-root',
  imports: [HeaderComponent, IncidentNoticeComponent, RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  private readonly language = inject(LanguageToken);
  private readonly country = inject(CountryToken);

  private destroy$ = new Subject<void>();
  private adClickIds = [
    'gclid',
    'wbraid',
    'gbraid',
    'msclkid',
    'fbclid',
    'SsCid',
    'TTCLID',
    'twclid',
    'rdt_cid',
  ];
  private readonly document = inject(DOCUMENT);
  private readonly meta = inject(Meta);
  private readonly titleService = inject(Title);

  constructor(
    private cookieService: CookieService,
    private eventService: EventService,
    private posthogService: PosthogService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
    private storage: StorageService,
    private viewportScroller: ViewportScroller,
  ) {
    this.applyHostLocaleToDocument();
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.injectGtm();
      this.posthogService.init();
      this.listenToPosthogPageviews();
      if (this.env.production) {
        this.listenToRoutes();
        this.initializeServerSideTracking();
      }
    }
  }

  /**
   * Host-driven locale: applied both on SSR (via Angular's server renderer,
   * which runs constructor synchronously) and on browser bootstrap. Also
   * injects a non-indexable robots meta tag for non-production deploys.
   */
  private applyHostLocaleToDocument(): void {
    if (this.document?.documentElement) {
      this.document.documentElement.lang = this.language;
    }
    this.titleService.setTitle(this.env.appName);
    if (DEPLOY_ENV !== 'prod') {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    }
  }

  /**
   * Inject Stape/GTM loader using the host-resolved server-side tagging
   * subdomain (ss.handyhand.<country>). Browser-only; runs after
   * bootstrap so the shell index.html stays host-neutral.
   */
  private injectGtm(): void {
    const gtmId = this.env.googleTagId;
    if (!gtmId || !gtmId.startsWith('GTM-') || gtmId === 'GTM-') {
      return;
    }
    try {
      const lang = this.country;
      const ssLoad = `https://load.ss.handyhand.${lang}`;
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `${ssLoad}/anqtvehav.js?8=aWQ9${btoa(gtmId)}&page=1`;
      this.document.head.appendChild(script);
    } catch (e) {
      console.error('GTM init failed', e);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private listenToPosthogPageviews() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((item) => {
      if (item instanceof NavigationEnd) {
        this.posthogService.trackPageview(item.urlAfterRedirects);
      }
    });
  }

  private listenToRoutes() {
    // Google Tracking
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((item) => {
      if (item instanceof NavigationEnd && isPlatformBrowser(this.platformId)) {
        this.eventService.customNewPageEvent(this.storage?.user, item.url);
        // Scroll to top on route change
        setTimeout(() => {
          // Scroll the specific container to top
          const container = document.querySelector('.handyhand_root_container');
          if (container) {
            container.scrollTop = 0;
          } else {
            // Fallback to page scroll if container not found
            this.viewportScroller.scrollToPosition([0, 0]);
            window.scrollTo(0, 0);
          }
        }, 50);
      }
    });
  }

  private initializeServerSideTracking() {
    try {
      this.route.queryParams
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          if (params) {
            // Store all query params (not just ad click IDs)
            this.storeAllQueryParams(params);
            // Continue with existing ad click tracking
            Object.entries(params).forEach(([key, value]) => {
              this.storeAdsClick(key, value);
            });
          }
        });
      // Read allQueryParams from cookie (set by server.ts)
      this.readAllQueryParamsFromCookie();
      this.adClickIds.forEach((id) => {
        const value = this.cookieService.getCookie(id);
        if (value) {
          this.storeAdsClick(id, value);
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  private storeAllQueryParams(params: Record<string, any>): void {
    if (!params || Object.keys(params).length === 0) {
      return;
    }
    const existingParams = this.storage.allQueryParams || {};
    // Merge with existing params (new params take precedence)
    this.storage.allQueryParams = { ...existingParams, ...params };
  }

  private readAllQueryParamsFromCookie(): void {
    const cookieValue = this.cookieService.getCookie('allQueryParams');
    if (cookieValue) {
      try {
        const paramsFromCookie = JSON.parse(cookieValue);
        const existingParams = this.storage.allQueryParams || {};
        // Cookie params are merged, but existing localStorage takes precedence
        this.storage.allQueryParams = {
          ...paramsFromCookie,
          ...existingParams,
        };
      } catch (error) {
        console.log('Error parsing allQueryParams cookie:', error);
      }
    }
  }

  storeAdsClick(key: string, value: any): void {
    if (!key || !value || !this.adClickIds.includes(key)) {
      return;
    }

    if (this.storage.adsClickObj) {
      if (Array.isArray(this.storage.adsClickObj)) {
        if (
          !this.storage.adsClickObj.some(
            (entry) =>
              entry.adsClickType === key && entry.adsClickValue === value,
          )
        ) {
          this.storage.adsClickObj.push({
            adsClickType: key,
            adsClickValue: value,
            clickTimeStamp: new Date().toISOString(),
          });
        }
      } else {
        if (
          this.storage.adsClickObj.adsClickType !== key &&
          this.storage.adsClickObj.adsClickValue !== value
        ) {
          this.storage.adsClickObj = [
            this.storage.adsClickObj,
            {
              adsClickType: key,
              adsClickValue: value,
              clickTimeStamp: new Date().toISOString(),
            },
          ];
        }
      }
    } else {
      this.storage.adsClickObj = [
        {
          adsClickType: key,
          adsClickValue: value,
          clickTimeStamp: new Date().toISOString(),
        },
      ];
    }
  }
}
